import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const TemplateSelector = ({ selectedTemplate, onTemplateSelect, onNext }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const credentialTemplates = [
    {
      id: 'education-diploma',
      name: 'Educational Diploma',
      category: 'education',
      description: 'Standard diploma template for educational institutions with academic credentials and verification',
      thumbnail: 'https://images.unsplash.com/photo-1523050854058-8df90110c9f1?w=400&h=300&fit=crop',
      fields: ['recipient_name', 'institution', 'degree', 'graduation_date', 'gpa'],
      format: 'JSON-LD',
      popularity: 95,
      isNew: false
    },
    {
      id: 'professional-certificate',
      name: 'Professional Certificate',
      category: 'professional',
      description: 'Industry-standard certificate template for professional skills and competencies verification',
      thumbnail: 'https://images.pexels.com/photos/3184292/pexels-photo-3184292.jpeg?w=400&h=300&fit=crop',
      fields: ['recipient_name', 'certification_body', 'skill_area', 'issue_date', 'expiry_date'],
      format: 'JWT',
      popularity: 88,
      isNew: true
    },
    {
      id: 'employment-verification',
      name: 'Employment Verification',
      category: 'employment',
      description: 'Comprehensive employment verification template with role details and tenure information',
      thumbnail: 'https://images.pixabay.com/photo/2016/11/19/14/00/code-1839406_1280.jpg?w=400&h=300&fit=crop',
      fields: ['employee_name', 'company', 'position', 'start_date', 'end_date', 'salary_range'],
      format: 'JSON-LD',
      popularity: 82,
      isNew: false
    },
    {
      id: 'medical-license',
      name: 'Medical License',
      category: 'healthcare',
      description: 'Secure medical license template with regulatory compliance and verification standards',
      thumbnail: 'https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=400&h=300&fit=crop',
      fields: ['practitioner_name', 'license_number', 'specialty', 'issuing_authority', 'expiry_date'],
      format: 'JSON-LD',
      popularity: 76,
      isNew: false
    },
    {
      id: 'government-id',
      name: 'Government ID',
      category: 'government',
      description: 'Official government identification template with enhanced security features and biometric data',
      thumbnail: 'https://images.pexels.com/photos/8112199/pexels-photo-8112199.jpeg?w=400&h=300&fit=crop',
      fields: ['full_name', 'id_number', 'date_of_birth', 'address', 'photo_hash'],
      format: 'JWT',
      popularity: 91,
      isNew: true
    },
    {
      id: 'membership-card',
      name: 'Membership Card',
      category: 'membership',
      description: 'Flexible membership template for organizations, clubs, and professional associations',
      thumbnail: 'https://images.pixabay.com/photo/2017/07/31/11/21/people-2557396_1280.jpg?w=400&h=300&fit=crop',
      fields: ['member_name', 'organization', 'membership_type', 'member_id', 'benefits'],
      format: 'JSON-LD',
      popularity: 69,
      isNew: false
    }
  ];

  const categories = [
    { id: 'all', name: 'All Templates', icon: 'Grid3X3' },
    { id: 'education', name: 'Education', icon: 'GraduationCap' },
    { id: 'professional', name: 'Professional', icon: 'Briefcase' },
    { id: 'employment', name: 'Employment', icon: 'Building2' },
    { id: 'healthcare', name: 'Healthcare', icon: 'Heart' },
    { id: 'government', name: 'Government', icon: 'Shield' },
    { id: 'membership', name: 'Membership', icon: 'Users' }
  ];

  const filteredTemplates = credentialTemplates?.filter(template => {
    const matchesSearch = template?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
                         template?.description?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || template?.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const handleTemplateSelect = (template) => {
    onTemplateSelect(template);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-semibold text-foreground">Select Credential Template</h2>
          <p className="text-muted-foreground mt-1">Choose from our library of verified credential templates</p>
        </div>
        <div className="flex items-center gap-2">
          <Icon name="Sparkles" size={16} className="text-accent" />
          <span className="text-sm text-muted-foreground">{filteredTemplates?.length} templates available</span>
        </div>
      </div>
      {/* Search and Filters */}
      <div className="flex flex-col lg:flex-row gap-4">
        <div className="flex-1 relative">
          <Icon name="Search" size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search templates by name or description..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e?.target?.value)}
            className="w-full pl-10 pr-4 py-2 border border-border rounded-lg bg-input text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>
        <div className="flex gap-2 overflow-x-auto pb-2 lg:pb-0">
          {categories?.map((category) => (
            <button
              key={category?.id}
              onClick={() => setSelectedCategory(category?.id)}
              className={`flex items-center px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                selectedCategory === category?.id
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              <Icon name={category?.icon} size={16} className="mr-2" />
              {category?.name}
            </button>
          ))}
        </div>
      </div>
      {/* Templates Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        {filteredTemplates?.map((template) => (
          <div
            key={template?.id}
            className={`credential-card cursor-pointer transition-all duration-200 hover:shadow-lg ${
              selectedTemplate?.id === template?.id
                ? 'ring-2 ring-primary bg-primary/5' :'hover:border-primary/20'
            }`}
            onClick={() => handleTemplateSelect(template)}
          >
            {/* Template Image */}
            <div className="relative h-40 mb-4 overflow-hidden rounded-lg bg-muted">
              <Image
                src={template?.thumbnail}
                alt={template?.name}
                className="w-full h-full object-cover"
              />
              <div className="absolute top-2 right-2 flex gap-2">
                {template?.isNew && (
                  <span className="px-2 py-1 bg-accent text-accent-foreground text-xs font-medium rounded-full">
                    New
                  </span>
                )}
                <span className="px-2 py-1 bg-background/90 text-foreground text-xs font-medium rounded-full">
                  {template?.format}
                </span>
              </div>
              <div className="absolute bottom-2 left-2 flex items-center gap-1 px-2 py-1 bg-background/90 rounded-full">
                <Icon name="TrendingUp" size={12} className="text-success" />
                <span className="text-xs font-medium text-foreground">{template?.popularity}%</span>
              </div>
            </div>

            {/* Template Info */}
            <div className="space-y-3">
              <div>
                <h3 className="font-semibold text-foreground">{template?.name}</h3>
                <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{template?.description}</p>
              </div>

              {/* Template Fields */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Icon name="FileText" size={14} className="text-muted-foreground" />
                  <span className="text-xs text-muted-foreground">{template?.fields?.length} fields</span>
                </div>
                <div className="flex flex-wrap gap-1">
                  {template?.fields?.slice(0, 3)?.map((field) => (
                    <span
                      key={field}
                      className="px-2 py-1 bg-muted text-muted-foreground text-xs rounded-md"
                    >
                      {field?.replace('_', ' ')}
                    </span>
                  ))}
                  {template?.fields?.length > 3 && (
                    <span className="px-2 py-1 bg-muted text-muted-foreground text-xs rounded-md">
                      +{template?.fields?.length - 3} more
                    </span>
                  )}
                </div>
              </div>

              {/* Selection Indicator */}
              {selectedTemplate?.id === template?.id && (
                <div className="flex items-center gap-2 text-primary">
                  <Icon name="CheckCircle" size={16} />
                  <span className="text-sm font-medium">Selected</span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      {/* Empty State */}
      {filteredTemplates?.length === 0 && (
        <div className="text-center py-12">
          <Icon name="Search" size={48} className="mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">No templates found</h3>
          <p className="text-muted-foreground mb-4">Try adjusting your search or filter criteria</p>
          <Button variant="outline" onClick={() => { setSearchQuery(''); setSelectedCategory('all'); }}>
            Clear Filters
          </Button>
        </div>
      )}
      {/* Action Buttons */}
      {selectedTemplate && (
        <div className="flex justify-end pt-6 border-t border-border">
          <Button onClick={onNext} iconName="ArrowRight" iconPosition="right">
            Continue with {selectedTemplate?.name}
          </Button>
        </div>
      )}
    </div>
  );
};

export default TemplateSelector;